import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { MagnifyingGlassIcon, StopCircleIcon, SparklesIcon } from '@heroicons/react/24/outline';
import videoUnderstandingApi, { IVideoUnderstandingJob } from '@/services/videoUnderstandingApi';
import { AudioPageLayout } from '@/components/Audio';
import VideoLibraryPicker, { SelectedVideo } from '@/components/Clips/VideoLibraryPicker';
import VideoUnderstandingDetailsDrawer, { StatusBadge } from '@/components/VideoUnderstanding/VideoUnderstandingDetailsDrawer';
import { targetLanguages } from '@/utils/videoTranslationConfig';
import Pagination, { paginate } from '@/components/Common/Pagination';

const HISTORY_ITEMS_PER_PAGE = 10;

export default function VideoUnderstandingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedVideo, setSelectedVideo] = useState<SelectedVideo | null>(null);
  const [title, setTitle] = useState('');
  const [language, setLanguage] = useState('auto');
  const [loading, setLoading] = useState(false);

  const [jobs, setJobs] = useState<Partial<IVideoUnderstandingJob>[]>([]);
  const [selectedJob, setSelectedJob] = useState<IVideoUnderstandingJob | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [historyPage, setHistoryPage] = useState(1);

  // Pre-fill from "Understand" actions on the Videos page (?videoS3Url=...&title=...)
  useEffect(() => {
    const qVideo = searchParams.get('videoS3Url') || '';
    const qTitle = searchParams.get('title') || '';
    if (qVideo) {
      setSelectedVideo({ url: qVideo, name: qTitle || 'Selected video' });
    }
    if (qTitle) setTitle(qTitle);
    if (qVideo) {
      document.getElementById('video-understanding-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [searchParams]);

  const loadJobs = async () => {
    try {
      const res = await videoUnderstandingApi.getJobs();
      if (res.data.success) {
        setJobs(res.data.jobs);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to load jobs');
    }
  };

  useEffect(() => {
    loadJobs();
  }, []);

  useEffect(() => {
    if (!jobs.some((job) => job.status === 'queued' || job.status === 'processing')) return;
    const interval = setInterval(loadJobs, 3000);
    return () => clearInterval(interval);
  }, [jobs]);

  const viewJob = async (jobId: string) => {
    try {
      const res = await videoUnderstandingApi.getJob(jobId);
      if (res.data.success) {
        setSelectedJob(res.data.job);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to load job details');
    }
  };

  const openJobDrawer = (jobId: string) => {
    viewJob(jobId);
    setDrawerOpen(true);
  };

  useEffect(() => {
    if (!drawerOpen || !selectedJob) return;
    if (selectedJob.status !== 'queued' && selectedJob.status !== 'processing') return;
    const timer = setTimeout(() => viewJob(selectedJob.jobId), 2000);
    return () => clearTimeout(timer);
  }, [drawerOpen, selectedJob]);

  const handleSelectVideo = (item: SelectedVideo) => {
    setSelectedVideo(item);
    if (!title) setTitle(item.name);
  };

  const handleClearVideo = () => setSelectedVideo(null);

  const handleCancelForm = () => {
    setSelectedVideo(null);
    setTitle('');
    setLanguage('auto');
    // Return to wherever this page was navigated from (Dashboard's quick-start,
    // the Videos page's "Understand" action, etc.) rather than staying here.
    navigate(-1);
  };

  const handleGenerate = async () => {
    if (!selectedVideo) {
      toast.error('Select a video to analyze');
      return;
    }

    setLoading(true);
    try {
      const res = await videoUnderstandingApi.createJob({
        title: title || undefined,
        videoS3Url: selectedVideo.url,
        language: language === 'auto' ? undefined : language,
      });

      if (res.data.success) {
        toast.success('Video understanding started');
        await loadJobs();
        openJobDrawer(res.data.jobId);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to start video understanding');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelJob = async (jobId: string) => {
    try {
      await videoUnderstandingApi.cancelJob(jobId);
      toast.success('Job cancelled');
      await loadJobs();
      if (selectedJob?.jobId === jobId) await viewJob(jobId);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to cancel job');
    }
  };

  const filteredJobs = useMemo(() => {
    const term = search.trim().toLowerCase();
    const selectedDate = dateFilter ? new Date(dateFilter) : null;
    const nextMinute = selectedDate ? new Date(selectedDate.getTime() + 60000) : null;
    return jobs.filter((job) => {
      const jobTitle = (job.title || job.videoS3Url || '').toLowerCase();
      const matchesSearch = !term || jobTitle.includes(term) || (job.jobId || '').toLowerCase().includes(term);
      const jobDate = job.createdAt ? new Date(job.createdAt) : null;
      const matchesDate = !selectedDate || (!!jobDate && jobDate >= selectedDate && jobDate < nextMinute!);
      return matchesSearch && matchesDate;
    });
  }, [jobs, search, dateFilter]);

  useEffect(() => {
    setHistoryPage(1);
  }, [search, dateFilter]);

  const paginatedJobs = paginate(filteredJobs, historyPage, HISTORY_ITEMS_PER_PAGE);
  const historyTotalPages = Math.ceil(filteredJobs.length / HISTORY_ITEMS_PER_PAGE);

  return (
    <>
      <AudioPageLayout
        title="AI Video Understanding"
        historyMode="drawer"
        formSection={
          <div id="video-understanding-form" className="space-y-6 bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <div>
              <h2 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">Analyze a video</h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Pick one of your videos to get a transcript, chapters, topics, a summary, and ranked highlight moments.
              </p>
            </div>

            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Video</label>
              <VideoLibraryPicker selected={selectedVideo} onSelect={handleSelectVideo} onClear={handleClearVideo} />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Title <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="My video"
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
                />
              </div>
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Language</label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
                >
                  <option value="auto">Auto-detect</option>
                  {targetLanguages.map((l) => (
                    <option key={l.code} value={l.code}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-gray-200 dark:border-gray-700 pt-4">
              <button
                type="button"
                onClick={handleCancelForm}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleGenerate}
                disabled={loading || !selectedVideo}
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <SparklesIcon className="h-4 w-4" />
                {loading ? 'Starting…' : 'Analyze Video'}
              </button>
            </div>
          </div>
        }
        historySection={
          <div className="flex flex-col h-full">
            <div className="p-4 space-y-3 border-b border-gray-200 dark:border-gray-700">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by title..."
                  className="block w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <input
                type="datetime-local"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {filteredJobs.length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400">No matching jobs.</p>}
              {paginatedJobs.map((job) => (
                <div
                  key={job.jobId}
                  className={`w-full text-left rounded-lg border p-4 transition-colors cursor-pointer ${
                    selectedJob?.jobId === job.jobId
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                  onClick={() => job.jobId && openJobDrawer(job.jobId)}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900 dark:text-white text-sm truncate pr-2">
                      {job.title || (job.jobId ? job.jobId.slice(0, 8) : 'Untitled')}
                    </span>
                    <div className="flex items-center gap-2">
                      {(job.status === 'queued' || job.status === 'processing') && (
                        <button
                          type="button"
                          title="Cancel job"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (job.jobId) handleCancelJob(job.jobId);
                          }}
                          className="p-1 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          <StopCircleIcon className="w-4 h-4" />
                        </button>
                      )}
                      {job.status && <StatusBadge status={job.status} />}
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    {job.status === 'processing' ? `${job.progress}% · ${job.currentStep}` : job.currentStep}
                  </div>
                  {job.createdAt && (
                    <div className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                      {new Date(job.createdAt).toLocaleString()}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <Pagination
              currentPage={historyPage}
              totalPages={historyTotalPages}
              onPageChange={setHistoryPage}
              totalItems={filteredJobs.length}
              itemsPerPage={HISTORY_ITEMS_PER_PAGE}
              className="border-t border-gray-200 dark:border-gray-700"
            />
          </div>
        }
      />

      {drawerOpen && selectedJob && (
        <VideoUnderstandingDetailsDrawer job={selectedJob} onClose={() => setDrawerOpen(false)} />
      )}
    </>
  );
}
